// src/pages/mock-data-management-dashboard/components/BulkOperationsPanel.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const BulkOperationsPanel = ({ onReset, onRefresh }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const getBulkActions = () => {
    return [
      {
        id: 'refresh_all',
        label: 'Refresh All Data',
        description: 'Regenerate all mock data with current settings',
        icon: 'RefreshCw',
        color: 'primary',
        confirmRequired: false
      },
      {
        id: 'reset_users',
        label: 'Reset Users',
        description: 'Clear all user data',
        icon: 'Users',
        color: 'warning',
        confirmRequired: true
      },
      {
        id: 'reset_biodata',
        label: 'Reset Biodata',
        description: 'Clear all biodata records',
        icon: 'FileText',
        color: 'warning',
        confirmRequired: true
      },
      {
        id: 'reset_documents',
        label: 'Reset Documents',
        description: 'Clear all document records',
        icon: 'FileImage',
        color: 'warning',
        confirmRequired: true
      },
      {
        id: 'reset_logs',
        label: 'Reset Logs',
        description: 'Clear all system logs',
        icon: 'Activity',
        color: 'warning',
        confirmRequired: true
      },
      {
        id: 'reset_all',
        label: 'Reset Everything',
        description: 'Clear all mock data (cannot be undone)',
        icon: 'Trash2',
        color: 'error',
        confirmRequired: true
      }
    ];
  };

  const getActionColor = (color) => {
    const colorMap = {
      primary: 'text-primary-600 bg-primary-50 hover:bg-primary-100',
      warning: 'text-warning-600 bg-warning-50 hover:bg-warning-100',
      error: 'text-error-600 bg-error-50 hover:bg-error-100'
    };
    return colorMap[color] || colorMap.primary;
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setShowDropdown(false);
    
    if (action.confirmRequired) {
      setShowConfirmModal(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = async (action) => {
    setIsProcessing(true);
    
    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      switch (action.id) {
        case 'refresh_all':
          onRefresh?.();
          break;
        case 'reset_users': onReset?.('users');
          break;
        case 'reset_biodata': onReset?.('biodataRecords');
          break;
        case 'reset_documents':
          onReset?.('documents');
          break;
        case 'reset_logs': onReset?.('systemLogs');
          break;
        case 'reset_all': onReset?.('all');
          break;
        default:
          console.warn('Unknown action:', action.id);
      }
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsProcessing(false);
      setShowConfirmModal(false);
      setSelectedAction(null);
    }
  };

  const handleConfirm = () => {
    if (selectedAction) {
      executeAction(selectedAction);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setSelectedAction(null);
  };

  const bulkActions = getBulkActions();

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isProcessing}
          className="btn btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Icon name="Settings" size={16} />
              <span>Bulk Actions</span>
              <Icon name="ChevronDown" size={14} />
            </>
          )}
        </button>

        {/* Dropdown Menu */}
        {showDropdown && (
          <div className="absolute right-0 mt-2 w-64 bg-surface rounded-lg shadow-xl border border-secondary-200 z-1001 animation-fade-in">
            <div className="p-4 border-b border-secondary-200">
              <h3 className="text-sm font-semibold text-text-primary mb-1">Bulk Operations</h3>
              <p className="text-xs text-text-secondary">
                Perform actions on multiple data sets
              </p>
            </div>
            
            <div className="py-2">
              {bulkActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleActionClick(action)}
                  className={`w-full flex items-center px-4 py-3 text-left transition-colors duration-150 ${
                    getActionColor(action.color)
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3">
                    <Icon name={action.icon} size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">
                      {action.label}
                    </div>
                    <div className="text-xs opacity-75">
                      {action.description}
                    </div>
                  </div>
                  {action.confirmRequired && (
                    <Icon name="AlertTriangle" size={14} className="text-warning-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Click outside handler */}
        {showDropdown && (
          <div
            className="fixed inset-0 z-1000"
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedAction && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1002">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4 animation-fade-in">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedAction.color === 'error' ? 'bg-error-100' : 'bg-warning-100'
                }`}>
                  <Icon 
                    name={selectedAction.icon} 
                    size={24} 
                    className={selectedAction.color === 'error' ? 'text-error-600' : 'text-warning-600'} 
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    Confirm {selectedAction.label}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {selectedAction.description}
                  </p>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg mb-6 ${
                selectedAction.color === 'error' ? 'bg-error-50 border border-error-200' : 'bg-warning-50 border border-warning-200'
              }`}>
                <div className="flex items-start space-x-2">
                  <Icon 
                    name="AlertTriangle" 
                    size={16} 
                    className={selectedAction.color === 'error' ? 'text-error-600 mt-0.5' : 'text-warning-600 mt-0.5'} 
                  />
                  <div className="text-sm">
                    <div className={`font-medium mb-1 ${
                      selectedAction.color === 'error' ? 'text-error-800' : 'text-warning-800'
                    }`}>
                      {selectedAction.color === 'error' ? 'Destructive Action' : 'Caution Required'}
                    </div>
                    <div className={selectedAction.color === 'error' ? 'text-error-700' : 'text-warning-700'}>
                      {selectedAction.id === 'reset_all' ?'This will permanently delete all mock data. This action cannot be undone.' :'This will clear the selected data. You can regenerate it afterwards.'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className={`btn flex items-center space-x-2 ${
                    selectedAction.color === 'error' ? 'btn-error' : 
                    selectedAction.color === 'warning' ? 'btn-warning' : 'btn-primary'
                  }`}
                >
                  <Icon name={selectedAction.icon} size={16} />
                  <span>Confirm {selectedAction.label.split(' ')[0]}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkOperationsPanel;